import { Injectable, Injector, Type, inject, signal } from '@angular/core';

import { NbxDialogConfig } from './nbx-dialog-config';
import { NbxDialogRef } from './nbx-dialog-ref';

export interface NbxDialogEntry {
  id: number;
  component: Type<unknown>;
  config: NbxDialogConfig;
  ref: NbxDialogRef;
  injector: Injector;
}

/**
 * Signal-driven dialog service.  Replaces PrimeNG's `DialogService`.
 *
 * Internals are 100% signal-based — no Subjects, no subscriptions:
 *  • Open dialogs live in the {@link dialogs} signal.
 *  • Each dialog's lifecycle is tracked through {@link NbxDialogRef.state}.
 *  • Cleanup happens through an `effect()` watching `ref.isClosed()`.
 *
 * Usage:
 * ```ts
 * const ref = dialogService.open(MyModalComponent, { data: payload });
 * effect(() => {
 *   if (ref.isClosed()) {
 *     console.log('result =', ref.value());
 *   }
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class NbxDialogService {
  private readonly _parentInjector = inject(Injector);
  private _nextId = 0;

  readonly dialogs = signal<NbxDialogEntry[]>([]);

  open<T = unknown>(component: Type<unknown>, config: NbxDialogConfig = {}): NbxDialogRef<T> {
    const id = this._nextId++;
    const ref = new NbxDialogRef<T>();

    const injector = Injector.create({
      parent: this._parentInjector,
      providers: [
        { provide: NbxDialogRef, useValue: ref },
        { provide: NbxDialogConfig, useValue: config },
      ],
    });

    const entry: NbxDialogEntry = { id, component, config, ref: ref as NbxDialogRef, injector };
    this.dialogs.update(stack => [...stack, entry]);

    ref.afterClosed(() => {
      this.dialogs.update(stack => stack.filter(e => e.id !== id));
    });

    return ref;
  }

  closeAll(): void {
    for (const entry of this.dialogs()) {
      entry.ref.close();
    }
  }
}
